import React from 'react';

import { getCurrencyLocale } from '@splitfiesta/i18n';
import { formatCurrencyWithCode } from '@splitfiesta/shared';

interface CurrencyDisplayProps {
  amount: number;
  currency: string;
  locale?: string;
  className?: string;
  showSymbol?: boolean;
  'data-testid'?: string;
  /** The pre-conversion amount, shown as a muted hint after the amount. */
  original?: { amount: number; currency: string };
}

export function CurrencyDisplay({
  amount,
  currency,
  locale,
  className = '',
  showSymbol: _showSymbol = true,
  'data-testid': testId,
  original,
}: CurrencyDisplayProps) {
  // Use currency-specific locale for proper formatting
  const currencyLocale = locale || getCurrencyLocale(currency);
  const formattedAmount = formatCurrencyWithCode(
    amount,
    currency,
    currencyLocale
  );
  // The hint is a DIFFERENT currency from the main amount (that's the
  // whole point of showing it) — it must derive its own locale from
  // `original.currency` the same way the main amount derives its locale
  // from `currency`, rather than reusing `currencyLocale` (which would
  // format the original amount with the group currency's grouping/decimal
  // conventions, e.g. a EUR hint in a COP group rendered COP-style).
  const originalLocale = original
    ? locale || getCurrencyLocale(original.currency)
    : currencyLocale;

  return (
    <span className={className} data-testid={testId}>
      {formattedAmount}
      {original && (
        <span
          data-testid="original-hint"
          className="block text-xs font-normal text-gray-500 dark:text-gray-400"
        >
          {formatCurrencyWithCode(
            original.amount,
            original.currency,
            originalLocale
          )}
        </span>
      )}
    </span>
  );
}

export default CurrencyDisplay;
